/**
 * selectionPaths — the ONE place that builds and reads whole-entity selection
 * paths (W12 Part B, Task B3).
 *
 * The create wizard's four selection steps write the SAME path list the
 * `CompositeDataPicker` used to own (`orchestrator.compositeProps
 * .savedSelections` / `onSelectionChange`), so `buildStrategyData`,
 * `handleSubmit`, the feasibility contract and the server payload keep their
 * shape — only the surface that produces the paths changed.
 *
 * A path is canonical when it names the WHOLE entity (`@task[t1]`); the field
 * sub-paths the old trees could produce (`@task[t1].name`) are still READ
 * (`idsOfPlane`) and are dropped by `replacePlane`, so a selection that came
 * from a saved strategy converges on the canonical form as soon as the operator
 * touches its plane.
 */

/**
 * The selection planes the wizard tables own.
 *
 * ⚠ `insight` (W13 IS2) is a READ-ONLY plane name: the «Insights» step's thesis
 * selection is FIRST-CLASS dialog state, never a selection path. `@insight[id]`
 * is the legacy spelling of the ANALYSIS pin — `insightSelectionFromPaths` and
 * `pathsToFirestoreFormat` parse it as `@insight[analysisId].<category>[thesis]`
 * — so writing thesis ids into the path list would corrupt the pin. Never pass
 * this plane to `replacePlane` or `idsOfPlane`; it exists so the plane-tagged
 * row types (`useWizardSourceRows`) can name the thesis plane.
 */
export type SelectionPlane =
  | 'task'
  | 'insight'
  | 'variable'
  | 'insightVariable'
  | 'review'
  | 'rating'
  | 'studyCase'
  | 'bigNumber';

/** `('task', 't1')` → `'@task[t1]'`. */
export const entityPath = (plane: SelectionPlane, id: string): string => `@${plane}[${id}]`;

/**
 * Anchored at the start so `@insightVariable[...]` is never read as a project
 * `@variable[...]` — the two planes carry different ids from different sources
 * (the same trap `pathsToFirestoreFormat` guards with its ordered branches).
 */
const planePattern = (plane: SelectionPlane): RegExp => new RegExp(`^@${plane}\\[([^\\]]+)\\]`);

/** Ids of one plane, in first-seen order, deduplicated. Field sub-paths count. */
export const idsOfPlane = (paths: string[], plane: SelectionPlane): string[] => {
  const pattern = planePattern(plane);
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const path of paths) {
    const id = path.match(pattern)?.[1];
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
};

/**
 * Drops EVERY path of `plane` (whole-entity and field sub-paths alike) and
 * appends one canonical path per id, in the given order. The other planes keep
 * their original order — a selection edit on one step never reshuffles another.
 */
/**
 * Fix wave 6 (FW6-R1) — the ONE writer of thesis paths, for the edit dialog's
 * preload of a strategy's FROZEN global narrowing.
 *
 * The insight plane is not a whole-entity plane (`replacePlane` would write
 * `@insight[thesisId]` and corrupt the analysis pin), so it gets its own helper:
 * every path of THIS analysis goes — the bare `@insight[a]` pin, its category and
 * thesis levels — and one `@insight[a].<category>[<id>]` per thesis is appended.
 * Paths of another analysis, and every other plane, keep their place and order.
 * The analysis pin survives: `pathsToFirestoreFormat` reads it off the prefix of
 * each thesis path.
 */
export const replaceInsightTheses = (
  paths: string[],
  analysisId: string,
  theses: Array<{ id: string; category: string }>,
): string[] => {
  const root = `@insight[${analysisId}]`;
  const kept = paths.filter((path) => path !== root && !path.startsWith(`${root}.`));
  const seen = new Set<string>();
  const appended: string[] = [];
  for (const thesis of theses) {
    if (seen.has(thesis.id)) continue;
    seen.add(thesis.id);
    appended.push(`${root}.${thesis.category}[${thesis.id}]`);
  }
  return [...kept, ...appended];
};

export const replacePlane = (paths: string[], plane: SelectionPlane, ids: string[]): string[] => {
  const pattern = planePattern(plane);
  const kept = paths.filter((path) => !pattern.test(path));
  const seen = new Set<string>();
  const appended: string[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    appended.push(entityPath(plane, id));
  }
  return [...kept, ...appended];
};
